import { XMLParser } from 'fast-xml-parser'
import { handleApiError } from '@/utils/error-handlers'

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
})

// BGG API base URL
const BGG_API_BASE_URL = 'https://boardgamegeek.com/xmlapi2'

// Retry configuration
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 500
const MAX_RETRY_DELAY = 3000

/**
 * Helper function to ensure image URLs are complete and valid
 * @param url - The URL of the image to check and fix
 * @returns A valid URL or placeholder
 */
function ensureValidImageUrl(url?: string): string {
  if (!url) return '/placeholder-game.png'

  // Make sure the URL uses HTTPS
  if (url.startsWith('http:')) {
    url = url.replace('http:', 'https:')
  }

  // Make sure the URL is absolute
  if (!url.startsWith('http')) {
    url = `https:${url}`
  }

  return url
}

/**
 * Type for BGG API search results
 */
export interface BGGSearchResult {
  id: string
  name: string
  yearPublished?: number
  thumbnail?: string
  image?: string
}

/**
 * Type for BGG API game details
 */
export interface BGGGameDetails {
  id: string
  name: string
  yearPublished?: number
  image?: string
  thumbnail?: string
  minPlayers?: number
  maxPlayers?: number
  minPlaytime?: number
  maxPlaytime?: number
  description?: string
  averageRating?: number
  complexity?: number // Average weight
  bggRank?: number
  recommendedAge?: number
  categories?: string[]
  mechanics?: string[]
  designers?: string[]
  artists?: string[]
  publishers?: string[]
}

/**
 * Helper function to add retry logic to API calls with exponential backoff
 * @param fetchFn - The fetch function to retry
 * @param maxRetries - Maximum number of retry attempts
 * @param initialDelay - Initial delay between retries in milliseconds
 * @param maxDelay - Maximum delay between retries in milliseconds
 * @returns The result of the fetch function or throws an error
 */
async function withRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries = MAX_RETRIES,
  initialDelay = INITIAL_RETRY_DELAY,
  maxDelay = MAX_RETRY_DELAY
): Promise<T> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`BGG API attempt ${attempt + 1}/${maxRetries + 1}`)
      return await fetchFn()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      console.error(`BGG API attempt ${attempt + 1} failed:`, lastError.message)

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        // Calculate delay with exponential backoff
        const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay)
        console.log(`Retrying in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Maximum retry attempts reached')
}

/**
 * Search the BoardGameGeek database for games by name
 * @param query - The search term
 * @param exact - Whether to match the name exactly
 * @param limit - Maximum number of results to return
 * @returns List of matching games with basic info
 */
export async function searchBGGGames(query: string, exact = false, limit = 10) {
  try {
    const url = `${BGG_API_BASE_URL}/search?query=${encodeURIComponent(query)}&type=boardgame${
      exact ? '&exact=1' : ''
    }`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/xml',
        'User-Agent': 'Boardgames Today App/1.0',
      },
    })
    if (!response.ok) {
      throw new Error(`BGG API search failed: ${response.status} ${response.statusText}`)
    }

    const xmlText = await response.text()
    const parsed = parser.parse(xmlText)

    // Handle case where no items are found
    if (!parsed.items || !parsed.items.item) {
      return { data: [], error: null }
    }

    // Convert result to array if only one item returned
    const items = Array.isArray(parsed.items.item) ? parsed.items.item : [parsed.items.item]

    // Map to our search result format
    const results: BGGSearchResult[] = items.slice(0, limit).map((item: any) => {
      // Name can be an array of objects, in which case we need to find the primary one
      let name = ''
      if (Array.isArray(item.name)) {
        const primaryName = item.name.find((n: any) => n.type === 'primary')
        name = primaryName ? primaryName.value : item.name[0].value
      } else if (item.name && item.name.value) {
        name = item.name.value
      } else if (typeof item.name === 'string') {
        name = item.name
      }

      return {
        id: item.id,
        name: name,
        yearPublished: item.yearpublished ? parseInt(item.yearpublished.value) : undefined,
      }
    })

    return { data: results, error: null }
  } catch (error) {
    return handleApiError(error, 'searching BoardGameGeek')
  }
}

/**
 * Get detailed information about a specific game from BoardGameGeek
 * @param gameId - The BGG ID of the game
 * @returns Detailed game information
 */
export async function getBGGGameDetails(gameId: string) {
  try {
    const url = `${BGG_API_BASE_URL}/thing?id=${gameId}&stats=1`

    const fetchData = async () => {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/xml',
          'User-Agent': 'Boardgames Today App/1.0',
        },
      })

      if (!response.ok) {
        throw new Error(`BGG API fetch failed: ${response.status} ${response.statusText}`)
      }

      const xmlText = await response.text()
      const parsed = parser.parse(xmlText)

      if (!parsed.items || !parsed.items.item) {
        throw new Error('No game data returned from BGG API')
      }

      return parsed
    }

    // Use retry logic
    const parsed = await withRetry(fetchData)

    // Use the first item if an array is returned
    const item = Array.isArray(parsed.items.item) ? parsed.items.item[0] : parsed.items.item

    // Extract name - handle different formats
    let name = ''
    if (Array.isArray(item.name)) {
      const primaryName = item.name.find((n: any) => n.type === 'primary')
      name = primaryName ? primaryName.value : item.name[0].value
    } else if (item.name && item.name.value) {
      name = item.name.value
    }

    // Extract description
    let description = item.description || ''

    // Extract ratings information
    const ratings = item.statistics?.ratings
    const averageRating = ratings?.average?.value ? parseFloat(ratings.average.value) : undefined
    const complexity = ratings?.averageweight?.value
      ? parseFloat(ratings.averageweight.value)
      : undefined

    // Extract rank
    let bggRank
    if (ratings?.ranks?.rank) {
      const ranks = Array.isArray(ratings.ranks.rank) ? ratings.ranks.rank : [ratings.ranks.rank]
      const boardgameRank = ranks.find((r: any) => r.name === 'boardgame')
      bggRank =
        boardgameRank && boardgameRank.value !== 'Not Ranked'
          ? parseInt(boardgameRank.value)
          : undefined
    }

    // Extract categories and mechanics
    const categories: string[] = []
    const mechanics: string[] = []
    const designers: string[] = []
    const artists: string[] = []
    const publishers: string[] = []

    if (item.link) {
      const links = Array.isArray(item.link) ? item.link : [item.link]
      links.forEach((link: any) => {
        if (link.type === 'boardgamecategory') {
          categories.push(link.value)
        } else if (link.type === 'boardgamemechanic') {
          mechanics.push(link.value)
        } else if (link.type === 'boardgamedesigner') {
          designers.push(link.value)
        } else if (link.type === 'boardgameartist') {
          artists.push(link.value)
        } else if (link.type === 'boardgamepublisher') {
          publishers.push(link.value)
        }
      })
    }

    const gameDetails: BGGGameDetails = {
      id: item.id,
      name: name,
      yearPublished: item.yearpublished?.value ? parseInt(item.yearpublished.value) : undefined,
      image: item.image ? ensureValidImageUrl(item.image) : undefined,
      thumbnail: item.thumbnail ? ensureValidImageUrl(item.thumbnail) : undefined,
      minPlayers: item.minplayers?.value ? parseInt(item.minplayers.value) : undefined,
      maxPlayers: item.maxplayers?.value ? parseInt(item.maxplayers.value) : undefined,
      minPlaytime: item.minplaytime?.value ? parseInt(item.minplaytime.value) : undefined,
      maxPlaytime: item.maxplaytime?.value ? parseInt(item.maxplaytime.value) : undefined,
      description: description,
      averageRating,
      complexity,
      bggRank,
      recommendedAge: item.minage?.value ? parseInt(item.minage.value) : undefined,
      categories,
      mechanics,
      designers,
      artists,
      publishers,
    }

    return { data: gameDetails, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching game details from BoardGameGeek')
  }
}

/**
 * Get top ranked games from BoardGameGeek
 * @param limit - Maximum number of top games to return
 * @returns List of top ranked games with their details
 */
export async function getTopRankedBGGGames(limit = 100) {
  try {
    // First get the hot games list
    const url = `${BGG_API_BASE_URL}/hot?type=boardgame`

    const fetchHotGames = async () => {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/xml',
          'User-Agent': 'Boardgames Today App/1.0',
        },
        mode: 'cors', // Enable CORS
      })

      if (!response.ok) {
        throw new Error(`BGG API hot games fetch failed: ${response.status} ${response.statusText}`)
      }

      const xmlText = await response.text()
      return parser.parse(xmlText)
    }

    // Use retry logic for hot games
    const parsed = await withRetry(fetchHotGames)

    // Handle case where no items are found
    if (!parsed.items || !parsed.items.item) {
      return { data: [], error: null }
    }

    // Convert result to array if only one item returned
    const items = Array.isArray(parsed.items.item) ? parsed.items.item : [parsed.items.item]

    // Get the IDs of the hot games
    const gameIds = items.slice(0, limit).map((item: any) => item.id)

    // Fetch game details in batches of 10
    const BATCH_SIZE = 10
    const gameDetails: any[] = []

    for (let i = 0; i < gameIds.length; i += BATCH_SIZE) {
      const batchIds = gameIds.slice(i, i + BATCH_SIZE).join(',')
      const detailsUrl = `${BGG_API_BASE_URL}/thing?id=${batchIds}&stats=1`

      const fetchGameDetails = async () => {
        const response = await fetch(detailsUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/xml',
            'User-Agent': 'Boardgames Today App/1.0',
          },
          mode: 'cors', // Enable CORS
        })

        if (!response.ok) {
          throw new Error(
            `BGG API game details fetch failed: ${response.status} ${response.statusText}`
          )
        }

        const xmlText = await response.text()
        console.log(`Batch ${i / BATCH_SIZE + 1} response:`, xmlText.substring(0, 200)) // Log first 200 chars of response
        return parser.parse(xmlText)
      }

      // Use retry logic for game details
      const detailsParsed = await withRetry(fetchGameDetails)

      if (detailsParsed.items?.item) {
        const batchDetails = Array.isArray(detailsParsed.items.item)
          ? detailsParsed.items.item
          : [detailsParsed.items.item]
        gameDetails.push(...batchDetails)
      }

      // Add a small delay between batches to avoid overwhelming the API
      if (i + BATCH_SIZE < gameIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    // Map the game details to our format
    const topGames = gameDetails.map((item: any, index: number) => {
      // Extract name
      let name = ''
      if (Array.isArray(item.name)) {
        const primaryName = item.name.find((n: any) => n.type === 'primary')
        name = primaryName ? primaryName.value : item.name[0].value
      } else if (item.name && item.name.value) {
        name = item.name.value
      }

      // Extract image URLs
      const image = item.image ? ensureValidImageUrl(item.image) : undefined
      const thumbnail = item.thumbnail ? ensureValidImageUrl(item.thumbnail) : undefined

      // Extract ratings
      const ratings = item.statistics?.ratings
      const averageRating = ratings?.average?.value ? parseFloat(ratings.average.value) : undefined

      return {
        id: item.id,
        name,
        yearPublished: item.yearpublished?.value ? parseInt(item.yearpublished.value) : undefined,
        image,
        thumbnail,
        averageRating,
        bggRank: index + 1,
      } as BGGGameDetails
    })

    return { data: topGames, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching top ranked games from BoardGameGeek')
  }
}
